// src/context/AudioProvider.jsx
import React, { createContext, useState, useEffect } from "react";

/**
 * Stub for creating/publishing a track. In real usage,
 * replace with Agora/Web Audio calls to fetch and create an audio track.
 */
async function publishMusicTrack(songObj, volume = 80) {
  // For demonstration, we'll return a fake object
  // that simulates playback methods (play, pause, stop, etc.).
  return {
    title: songObj.title,
    duration: songObj.duration || 180,
    currentState: "stopped",
    volume,
    currentTime: 0,
    _startTime: 0,

    play() {
      this.currentState = "playing";
      this._startTime = Date.now() - this.currentTime * 1000;
    },
    pause() {
      if (this.currentState === "playing") {
        this.currentTime = (Date.now() - this._startTime) / 1000;
        this.currentState = "paused";
      }
    },
    resume() {
      if (this.currentState === "paused") {
        this._startTime = Date.now() - this.currentTime * 1000;
        this.currentState = "playing";
      }
    },
    stop() {
      this.currentState = "stopped";
      this.currentTime = 0;
    },
    seek(timeSec) {
      this.currentTime = timeSec;
      if (this.currentState === "playing") {
        this._startTime = Date.now() - timeSec * 1000;
      }
    },
    getCurrentTime() {
      if (this.currentState === "playing") {
        return (Date.now() - this._startTime) / 1000;
      }
      return this.currentTime;
    },
    setVolume(vol) {
      this.volume = vol;
    },
  };
}

// Create the AudioContext
export const AudioContext = createContext(null);

/**
 * AudioProvider component
 * - Maintains playlist, current track, time, volume, etc.
 * - Exposes methods (playSongByIndex, pauseSong, resumeSong, nextSong, etc.)
 *   so child components can control playback through context.
 */
export function AudioProvider({ children }) {
  const [playlist, setPlaylist] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [track, setTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(80);

  /**
   * Example initialization: populate an initial playlist.
   * (In a real app, you might fetch songs from an API or pass them as a prop.)
   */
  useEffect(() => {
    const sampleSongs = [
      { title: "Song A", duration: 200 },
      { title: "Song B", duration: 150 },
      { title: "Song C", duration: 180 },
    ];
    setPlaylist(sampleSongs);
  }, []);

  /**
   * Keep updating currentTime every second if a track is playing.
   * If currentTime exceeds the track duration, stop.
   */
  useEffect(() => {
    const interval = setInterval(() => {
      if (track && track.currentState === "playing") {
        const elapsed = track.getCurrentTime();
        setCurrentTime(elapsed);

        // If we've reached or passed the end, stop.
        if (elapsed >= track.duration) {
          stopSong();
        }
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [track]);

  // ==================== ACTIONS ====================

  async function playSongByIndex(songIndex) {
    if (songIndex < 0 || songIndex >= playlist.length) {
      console.log("[AudioProvider] Invalid song index:", songIndex);
      return;
    }
    // Stop any currently playing track
    stopSong();

    // Create/publish a new track
    const chosenSong = playlist[songIndex];
    const newTrack = await publishMusicTrack(chosenSong, volume);
    setTrack(newTrack);
    setCurrentIndex(songIndex);

    // Start playback
    newTrack.play();
    setIsPlaying(true);
  }

  function pauseSong() {
    if (!track) return;
    track.pause();
    setIsPlaying(false);
  }

  function resumeSong() {
    if (!track) return;
    track.resume();
    setIsPlaying(true);
  }

  function stopSong() {
    if (track) {
      track.stop();
    }
    setIsPlaying(false);
    setTrack(null);
    setCurrentIndex(-1);
    setCurrentTime(0);
  }

  function nextSong() {
    if (currentIndex + 1 < playlist.length) {
      playSongByIndex(currentIndex + 1);
    }
  }

  function prevSong() {
    if (currentIndex - 1 >= 0) {
      playSongByIndex(currentIndex - 1);
    }
  }

  function seekSong(seconds) {
    if (!track) return;
    track.seek(seconds);
    setCurrentTime(seconds);
  }

  function handleVolumeChange(val) {
    setVolume(val);
    if (track) {
      track.setVolume(val);
    }
  }

  // Expose state + actions through context
  const value = {
    playlist,
    setPlaylist,    // in case you want to override or add new songs
    currentIndex,
    track,
    isPlaying,
    currentTime,
    volume,

    playSongByIndex,
    pauseSong,
    resumeSong,
    stopSong,
    nextSong,
    prevSong,
    seekSong,
    setVolume: handleVolumeChange,
  };

  return (
    <AudioContext.Provider value={value}>
      {children}
    </AudioContext.Provider>
  );
}
